import * as React from "react";
import { connect } from "react-redux";
import Helmet from "react-helmet";
import styled from "styled-components";
import {
  DisplayMedium,
  Thirds,
  Center,
  TabBar,
  Tab,
  TertiaryNav,
  spacing,
} from "@conserveit/blocks";
import { navigate } from "../../utils/routes/navigate";
import * as routes from "../../utils/routes";
import { RouteComponentProps, Redirect } from "react-router";
import ProjectSettings from "./ProjectSettings";
import { RulesProvider } from "../../context/settings/rules";
import { ConfigProvider } from "../../context/settings/config";

const TabBarNav = styled(TabBar)`
  padding: 0 ${spacing.extraLoose};
`;

const StyledThirds = styled(Thirds)`
  padding: ${spacing.default} 0;
`;

const LINKS = {
  [routes.InsightType.Insights]: {
    label: "Insights",
    value: routes.InsightType.Insights,
    href: routes.SETTINGS_INSIGHTS,
    index: 0,
  },
  [routes.InsightType.Monitoring]: {
    label: "Monitoring",
    value: routes.InsightType.Monitoring,
    href: routes.SETTINGS_MONITORING,
    index: 1,
  },
};

interface MatchParams {
  insightType: routes.InsightType;
}

interface OwnProps {
  appSetupComplete: boolean;
  sites: Insite.AppState["userSites"]["sites"];
  projects: Insite.AppState["userSites"]["projects"];
  user: Insite.AppState["auth"]["user"];
}

type Props = RouteComponentProps<MatchParams> & OwnProps;

const ProjectSettingsView: React.FC<Props> = ({
  match,
  appSetupComplete,
  user,
  sites,
  projects,
}) => {
  if (!appSetupComplete) return null;

  const { insightType } = match.params;
  const link = LINKS[insightType];

  // * 404 redirect if :insightType query parameter is invalid
  if (!link) {
    return <Redirect to="/404" />;
  }

  const current = link.index;
  const siteList: Insite.Site[] = React.useMemo(
    () => Object.values(sites),
    [sites]
  );
  const projectList: Insite.Project[] = React.useMemo(
    () => Object.values(projects),
    [projects]
  );

  return (
    <React.Fragment>
      <Helmet title="Project Settings" />
      <TertiaryNav hasBottomBorder>
        <StyledThirds>
          <Center>
            <DisplayMedium>Project Settings</DisplayMedium>
          </Center>
        </StyledThirds>
      </TertiaryNav>
      <TertiaryNav>
        <TabBarNav current={current}>
          {Object.values(LINKS).map(({ href, label }) => (
            <Tab key={href} onClick={navigate(href)}>
              {label}
            </Tab>
          ))}
        </TabBarNav>
      </TertiaryNav>
      <ConfigProvider>
        <RulesProvider>
          <ProjectSettings
            insightType={insightType}
            user={user}
            sites={sites}
            siteList={siteList}
            projectList={projectList}
          />
        </RulesProvider>
      </ConfigProvider>
    </React.Fragment>
  );
};

function mapStateToProps(state: Insite.AppState) {
  return {
    appSetupComplete: state.appSetup.appSetupComplete,
    sites: state.userSites.sites,
    projects: state.userSites.projects,
    user: state.auth.user,
  };
}

export default connect(mapStateToProps)(ProjectSettingsView);
